import { useCallback, useContext, useMemo, useRef, useState } from 'react';

import cn from 'classnames';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import { Slider, type SliderRef } from 'ui/atoms/Slider/Slider';
import { CardsPosts } from 'ui/atoms/CardsPosts/CardsPosts';
import { OfferBlock } from 'ui/atoms/OfferBlock/OfferBlock';
import { Button } from 'ui/atoms/Button/Button';
import { Icon } from 'ui/atoms/Icon/Icon';

import styles from './FeaturedContent.module.scss';

const baseClass = 'featured-content';

type Props = {
  title: string;
  description: string;
  theme?: 'dark' | 'light';
  className?: string;
};

export const FeaturedContent = ({ title, description, theme = 'dark', className }: Props) => {
  const { additionals, secondaryNavigation } = useContext(ContentContext);
  const { t } = useInt();

  const sliderRef = useRef<SliderRef>(null);

  const [hasLeft, setHasLeft] = useState(false);
  const [hasRight, setHasRight] = useState(false);

  const onTransitionsChange = useCallback((left: boolean, right: boolean) => {
    setHasLeft(left);
    setHasRight(right);
  }, []);

  const onPrev = useCallback(() => sliderRef.current?.onLeftTransition(), []);
  const onNext = useCallback(() => sliderRef.current?.onRightTransition(), []);

  const renderedSliderItems = useMemo(() => {
    return (
      <CardsPosts
        data={additionals.articlesList?.data || []}
        hasBackground
        className={theme === 'light' ? styles.cardsList : undefined}
      />
    );
  }, [additionals.articlesList?.data, theme]);

  return (
    <section className={cn(styles[`${baseClass}-wrapper`], theme === 'light' && styles.light, className)}>
      <div className={styles[`${baseClass}-container`]}>
        <OfferBlock
          kicker={
            theme === 'light' ? (
              <span style={{ color: '#0550e2' }}>{t('Featured content')}</span>
            ) : (
              t('Featured content')
            )
          }
          title={title}
          description={description}
          tools={
            <div className={styles[`${baseClass}-actions`]}>
              <div className={styles[`${baseClass}-arrows`]}>
                <Button
                  disabled={!hasLeft}
                  onClick={onPrev}
                  size="xl"
                  loadingDelay={0}
                  type={theme === 'light' ? 'dark-outline' : 'white-outline'}
                  className={styles[`${baseClass}-arrow`]}
                >
                  <Icon type="arrow-8" />
                </Button>

                <Button
                  disabled={!hasRight}
                  onClick={onNext}
                  size="xl"
                  loadingDelay={0}
                  type={theme === 'light' ? 'dark-outline' : 'white-outline'}
                  className={styles[`${baseClass}-arrow`]}
                >
                  <Icon type="arrow-9" />
                </Button>
              </div>

              <Button
                size="xl"
                type={theme === 'light' ? 'dark' : 'transparent'}
                route={secondaryNavigation?.blog?.slug}
                className={styles[`${baseClass}-cta`]}
              >
                {t('More insights')}
              </Button>
            </div>
          }
        />

        <Slider
          ref={sliderRef}
          onTransitionsChange={onTransitionsChange}
          hasArrows={false}
          className={styles[`${baseClass}-slider`]}
        >
          {renderedSliderItems}
        </Slider>
      </div>
    </section>
  );
};
